// Centralised blog post data with full content for /blog and /blog/:slug pages.

export const BLOG_POSTS_FULL = [
  {
    slug: "app-store-optimization-2026-keyword-stack",
    category: "ASO Playbook",
    title:
      "App Store Optimization in 2026: the keyword stack that still works",
    excerpt:
      "Why long-tail keyword clustering beats single-keyword chasing on the modern App Store algorithm.",
    readTime: "8 min read",
    publishedAt: "2026-04-12",
    author: "Aarav Sharma · Senior ASO Strategist",
    cover:
      "https://images.unsplash.com/photo-1770013277247-ab7a08d3d9ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGNvbXBhbnklMjBsb2dvfGVufDB8fHx8MTc3NzYyOTgwOHww&ixlib=rb-4.1.0&q=85",
    content: [
      { type: "p", text: "Single-keyword chasing is dead. The 2026 App Store algorithm rewards apps that own a cluster of long-tail intent — 8 to 12 closely related queries — rather than apps that fight tooth and nail for one head term." },
      { type: "h2", text: "1. Cluster, don't chase" },
      { type: "p", text: "Pick a category seed (e.g. 'meditation app'). Map every long-tail variant: 'sleep meditation', 'breathing exercise', 'guided sleep stories', 'anxiety relief app'. Rank for the cluster — not the seed." },
      { type: "h2", text: "2. Subtitle is the new title" },
      { type: "p", text: "Apple's algorithm has shifted weight toward subtitle keywords in 2025–26. We've tested it across 14 client apps: keyword in subtitle now outperforms keyword in title for impressions in 9 out of 14 cases." },
      { type: "h2", text: "3. The 100-character keyword field still matters" },
      { type: "p", text: "Yes, even in 2026. Don't waste it on words already in your title or subtitle — use it for synonym variants and competitor brand terms (where allowed)." },
      { type: "h2", text: "4. Description ≠ keywords on iOS, but it does on Google Play" },
      { type: "p", text: "On Apple, description is for humans. On Google Play, every word in the long description is indexed — write for both audiences with platform-specific copy." },
      { type: "h2", text: "5. Localize, then localize again" },
      { type: "p", text: "Top-5 markets you're not in are losing you 30–50% of your global TAM. Localize iteratively: Tier-1 first (USA, UK, India, Brazil, Germany), then Tier-2." },
    ],
  },
  {
    slug: "rank-50-to-1-90-day-aso-sprint",
    category: "Growth",
    title: "From rank #50 to #1: a 90-day ASO sprint, fully open-sourced",
    excerpt:
      "Inside the screenshot, metadata and review-loop changes we ran for a UAE FinTech client.",
    readTime: "11 min read",
    publishedAt: "2026-03-28",
    author: "Riya Mehta · Growth Lead",
    cover:
      "https://images.unsplash.com/photo-1770210217380-d78a69acdc77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGNvbXBhbnklMjBsb2dvfGVufDB8fHx8MTc3NzYyOTgwOHww&ixlib=rb-4.1.0&q=85",
    content: [
      { type: "p", text: "When PaySprint engaged us in January, they ranked #38 on Google Play UAE for 'mobile banking'. Ninety days later they were #2 — with a +412% lift in organic installs. Here's exactly what we did, week by week." },
      { type: "h2", text: "Weeks 1–2: Audit & Strategy" },
      { type: "p", text: "We ran our 47-point audit. Three things stood out: keyword stack was Anglicised when 60% of UAE users search in Arabic; screenshots were UI-only with no benefit copy; rating was 3.9 with 18% reply rate on 1–3★ reviews." },
      { type: "h2", text: "Weeks 3–4: Listing rewrite" },
      { type: "p", text: "Built a bilingual keyword stack (Arabic primary, English secondary). Rewrote title, subtitle and 100-char keyword field. Submitted listing on Wednesday — Google indexed within 36 hours." },
      { type: "h2", text: "Weeks 5–8: Screenshot rebuild + A/B" },
      { type: "p", text: "Five new screenshot sets: benefit headline + product shot + social proof. A/B tested via Play Console. Winner lifted CVR from 18% to 31% in store-listing tests." },
      { type: "h2", text: "Weeks 9–12: Reply ops + scale" },
      { type: "p", text: "Stood up our reply-ops desk: every 1–3★ review answered within 24h with a fix or follow-up. Rating climbed from 3.9 → 4.5 in 6 weeks. By week 12 we were #2 organically with no paid spend." },
    ],
  },
  {
    slug: "geo-generative-engine-optimization-for-apps",
    category: "GEO",
    title:
      "Generative Engine Optimization for apps: the new ASO frontier",
    excerpt:
      "How to get your app cited by ChatGPT, Claude, Gemini and Perplexity — and why it matters in 2026.",
    readTime: "9 min read",
    publishedAt: "2026-02-14",
    author: "Vikram Joshi · GEO Practice Lead",
    cover:
      "https://images.pexels.com/photos/13597064/pexels-photo-13597064.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    content: [
      { type: "p", text: "65% of Gen Z users now ask an LLM for app recommendations before opening the App Store. If your app isn't cited inside ChatGPT, Claude, Gemini or Perplexity answers, you're invisible to a fast-growing slice of intent." },
      { type: "h2", text: "GEO ≠ SEO" },
      { type: "p", text: "GEO optimizes for citation, not click. The signals are different: structured fact density, knowledge-graph entity presence, and citation-worthy public content (research reports, comparison pages, founder interviews)." },
      { type: "h2", text: "The 6 GEO pillars" },
      { type: "p", text: "Canonical content engineering, knowledge graph presence, citation-worthy assets, LLM answer monitoring, prompt surface optimization, and model-specific tuning. We cover each in depth in our GEO service page." },
      { type: "h2", text: "Why now" },
      { type: "p", text: "Early movers compound. The apps cited by ChatGPT in 2026 will own the answer slot for years — because LLMs reuse the same trusted sources. Get there early, lock in the share." },
    ],
  },
  {
    slug: "ios-custom-product-pages-2026-ab-testing",
    category: "ASO Playbook",
    title: "iOS Custom Product Pages: the 2026 A/B testing guide",
    excerpt:
      "How to ship 3 CPPs, measure conversion and never kill a winning variant by accident.",
    readTime: "7 min read",
    publishedAt: "2026-04-30",
    author: "Aarav Sharma · Senior ASO Strategist",
    cover:
      "https://images.unsplash.com/photo-1770013277247-ab7a08d3d9ba?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGNvbXBhbnklMjBsb2dvfGVufDB8fHx8MTc3NzYyOTgwOHww&ixlib=rb-4.1.0&q=85",
    content: [
      { type: "p", text: "Custom Product Pages (CPPs) on iOS are still under-used in 2026. Used right, they lift Apple Search Ads ROAS 30–50% by serving traffic-intent-matched creative." },
      { type: "h2", text: "Three CPPs is the sweet spot" },
      { type: "p", text: "More than three and you can't gather enough impressions per variant. We recommend: default page, ASA brand-keyword page, ASA competitor-keyword page." },
      { type: "h2", text: "Statistical significance, not vibes" },
      { type: "p", text: "Wait for 2,000+ impressions per variant before declaring a winner. Apple's first-party measurement is solid — trust it." },
      { type: "h2", text: "Don't overwrite winners" },
      { type: "p", text: "Common rookie move: A/B winner found, agency 'iterates' before locking it as default. Lock it. Iterate elsewhere." },
    ],
  },
  {
    slug: "push-notification-copy-that-doesnt-feel-like-push",
    category: "Retention",
    title:
      "Push notification copy that doesn't feel like push notification copy",
    excerpt:
      "Seven copy frames we use to lift D7 retention without increasing opt-out.",
    readTime: "6 min read",
    publishedAt: "2026-03-05",
    author: "Diya Kapoor · Retention Engineer",
    cover:
      "https://images.pexels.com/photos/13597064/pexels-photo-13597064.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    content: [
      { type: "p", text: "Push opt-out happens because users feel marketed-to, not because of frequency. The fix is in the copy — we have seven frames that consistently land." },
      { type: "h2", text: "Frame 1: The continuation" },
      { type: "p", text: "'You left a 4-minute meditation half-done — finish it now?' Picks up where the user left off. Almost zero opt-out." },
      { type: "h2", text: "Frame 2: The social proof" },
      { type: "p", text: "'12 friends are streaking this week — join them?' Use real numbers, not estimates." },
      { type: "h2", text: "Frame 3: The 'time-block'" },
      { type: "p", text: "'Your 6pm wind-down is in 5 minutes.' Tied to user-set schedules — not random." },
    ],
  },
  {
    slug: "apple-search-ads-bidding-2026-three-account",
    category: "UA",
    title: "Apple Search Ads bidding in 2026 — the 3-account structure",
    excerpt:
      "Discovery, Exact, Competitor. Why the 3-account split still beats single-account sprawl.",
    readTime: "9 min read",
    publishedAt: "2026-02-22",
    author: "Sneha Kulkarni · UA Manager",
    cover:
      "https://images.unsplash.com/photo-1770210217380-d78a69acdc77?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGNvbXBhbnklMjBsb2dvfGVufDB8fHx8MTc3NzYyOTgwOHww&ixlib=rb-4.1.0&q=85",
    content: [
      { type: "p", text: "Apple Search Ads matured a lot in 2025. Custom Product Pages, ad variations, in-app event ads. But the most under-rated lever is still account structure." },
      { type: "h2", text: "Three accounts, three jobs" },
      { type: "p", text: "Discovery account hunts new keywords. Exact account scales known winners. Competitor account chases brand terms. Each has its own CPI ceiling and bid logic." },
      { type: "h2", text: "Why not one account?" },
      { type: "p", text: "Apple's keyword auctions don't separate intent. Mixing brand and discovery in one account means your bids skew toward the lower-funnel terms — and you starve discovery." },
    ],
  },
];

export function getPostBySlug(slug) {
  return BLOG_POSTS_FULL.find((p) => p.slug === slug);
}
